import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { HelpCircle, Lightbulb, BookOpen, ArrowRight } from "lucide-react";
import { pageHelpFor } from "@/help/helpContent";

// Top-bar "?" button that opens a contextual help panel for the current page.
export default function HelpPanel() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const help = pageHelpFor(loc.pathname);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button aria-label="Help" data-testid="help-button"
          className="rounded-full p-2 hover:bg-slate-100 transition-colors text-slate-500">
          <HelpCircle size={19} />
        </button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="help-panel">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-[#0B1A2F]">
            <span className="rounded-lg bg-[#2EA3FD]/10 text-[#2EA3FD] p-1.5"><HelpCircle size={18} /></span>
            {help ? `Help · ${help.title}` : "Help"}
          </SheetTitle>
          <SheetDescription>Tips for this page, plus a link to the full Help centre.</SheetDescription>
        </SheetHeader>

        <div className="mt-5 space-y-5">
          {help ? (
            <>
              <p className="text-sm text-slate-600 leading-relaxed" data-testid="help-panel-intro">{help.intro}</p>
              <div className="space-y-2.5">
                {help.tips.map((t, i) => (
                  <div key={i} className="flex items-start gap-2.5 rounded-lg bg-slate-50 px-3 py-2.5" data-testid={`help-tip-${i}`}>
                    <Lightbulb size={15} className="text-[#2EA3FD] mt-0.5 shrink-0" />
                    <p className="text-sm text-slate-600">{t}</p>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-500">Browse the Help centre for guides on every part of the portal.</p>
          )}

          <Link to="/help" onClick={() => setOpen(false)} data-testid="help-open-centre"
            className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 hover:border-[#2EA3FD] hover:bg-[#F5FAFF] px-4 py-3 transition-colors">
            <span className="flex items-center gap-2.5">
              <BookOpen size={17} className="text-[#2EA3FD]" />
              <span className="text-sm font-medium text-[#0B1A2F]">Open the full Help centre</span>
            </span>
            <ArrowRight size={16} className="text-slate-400" />
          </Link>
        </div>
      </SheetContent>
    </Sheet>
  );
}
